import { useState } from 'react';

import { TAILWIND_FAMILIES, TAILWIND_PALETTE, TAILWIND_SHADES } from '@/lib/tailwindPalette';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@startupquickstart/frontend';

interface ColorSwatchPickerProps {
  /** Current hex value ('' when unset — renders a neutral placeholder). */
  value: string;
  onChange: (hex: string) => void;
  ariaLabel: string;
  /** Trigger sizing/positioning overrides (defaults to h-10 w-12). */
  className?: string;
  /**
   * The color actually used when `value` is empty (the app default). Shown on
   * the trigger so admins see what "blank" resolves to; without it an empty
   * value renders a striped placeholder.
   */
  placeholderColor?: string;
}

/**
 * Color picker for branding fields: a swatch trigger opening the full default
 * Tailwind palette (22 families × 11 shades) so organizers pick from
 * designed-to-look-good colors by default, plus a native picker for fully
 * custom values. The paired hex input (owned by the caller) remains the
 * source of truth — this only ever emits valid hex.
 */
export function ColorSwatchPicker({
  value,
  onChange,
  ariaLabel,
  className,
  placeholderColor,
}: ColorSwatchPickerProps) {
  const [open, setOpen] = useState(false);
  const normalized = value.trim().toLowerCase();
  const hasColor = /^#[0-9a-f]{6}$/.test(normalized);
  const shownColor = hasColor ? normalized : placeholderColor;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          title={!hasColor && placeholderColor ? `Default: ${placeholderColor}` : undefined}
          className={cn(
            'h-10 w-12 shrink-0 cursor-pointer rounded border bg-background transition-shadow hover:ring-2 hover:ring-ring/30',
            !shownColor && 'bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,var(--border)_4px,var(--border)_6px)]',
            className,
          )}
          style={shownColor ? { backgroundColor: shownColor } : undefined}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-3">
        <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
          {TAILWIND_FAMILIES.map((family) => (
            <div key={family} className="flex items-center gap-1">
              {TAILWIND_SHADES.map((shade) => {
                const hex = TAILWIND_PALETTE[family][shade];
                const selected = normalized === hex;
                return (
                  <button
                    key={shade}
                    type="button"
                    title={`${family}-${shade} · ${hex}`}
                    aria-label={`${family}-${shade}`}
                    onClick={() => {
                      onChange(hex);
                      setOpen(false);
                    }}
                    className={cn(
                      'h-5 w-5 shrink-0 rounded-sm transition-transform hover:scale-125 hover:ring-1 hover:ring-ring',
                      selected && 'ring-2 ring-ring ring-offset-1',
                    )}
                    style={{ backgroundColor: hex }}
                  />
                );
              })}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 border-t pt-3">
          <span className="text-xs text-muted-foreground">Need a different shade?</span>
          <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-foreground">
            Custom
            <input
              type="color"
              aria-label={`${ariaLabel} — custom color`}
              value={hasColor ? normalized : '#cccccc'}
              onChange={(e) => onChange(e.target.value)}
              className="h-7 w-9 cursor-pointer rounded border bg-background"
            />
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}
